import { ChangeEvent, FC, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Text,
  Heading,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
  InputGroup,
  InputLeftElement,
  Input,
  useDisclosure,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  VStack,
  Th,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import { useAbility } from '@casl/react';

import PlusIcon from '../../../assets/icons/plus.svg';
import { ReactComponent as RedTrashIcon } from '../../../assets/icons/red-trash.svg';
import RedTrashImage from '../../../assets/icons/red-trash.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ReactComponent as ChevronUpIcon } from '../../../assets/icons/chevron-up.svg';
import { ReactComponent as ChevronDownIcon } from '../../../assets/icons/chevron-down.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import {
  useDeleteTeamMembersMutation,
  useResendVerificationEmailMutation,
  displayNotification,
  useGetPartnersQuery,
} from '../../../services';
import {
  // FilterDrawer,
  CenteredSpinner,
  RoundedBadge,
  Paginator,
  TableHeader,
  UserData,
} from '../../../components';
import { AbilityContext, Can } from '../../../providers';
import { useTypedSelector } from '../../../hooks';
// import { userFilters } from '../../../utils';
import { TeamMemberStatus } from '../../../models';
import { useForm, useFieldArray } from 'react-hook-form';
import { DeleteUsersModal } from '../components';
import { UserMutation } from '../models';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

export const Partners: FC = () => {
  const ability = useAbility(AbilityContext);
  const { status } = useParams();
  const [searchTerm, setSearchTerm] = useState<string>();
  const [page, setPage] = useState(0);

  // const [filters, setFilters] = useState<{ status?: Status }>({
  //   status: undefined,
  // });
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  // const { isOpen, onOpen, onClose } = useDisclosure();

  const navigate = useNavigate();

  const { userDetails } = useTypedSelector(state => state.auth);

  const {
    data,
    isLoading,
    isFetching,
    // , refetch
  } = useGetPartnersQuery(
    {
      page,
      name: debouncedSearchTerm,
      companyId: Number(userDetails?.companyBasicInfo?.id),
      // ...filters,
    },
    {
      skip: !userDetails?.companyBasicInfo?.id,
    }
  );

  const [deleteTeamMembers] = useDeleteTeamMembersMutation();
  const [resendVerificationEmail] = useResendVerificationEmailMutation();

  const { control, getValues } = useForm<{
    companies: UserMutation[];
  }>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'companies',
  });

  const {
    isOpen: isDeleteCompaniesModalOpen,
    onOpen: onOpenDeleteCompaniesModal,
    onClose: onCloseDeleteCompaniesModal,
  } = useDisclosure();

  // const handleFilterDrawerClose = (filterValues?: { status: Status }) => {
  //   onClose();
  //   if (!filterValues) return;

  //   setFilters({ status: filterValues.status });

  //   void refetch();
  // };

  // const handleRemoveFilter = (filterKey: keyof typeof filters) => {
  //   setFilters({ ...filters, [filterKey]: undefined });

  //   void refetch();
  // };

  const handelToggleCheckedCompanies = (
    e: ChangeEvent<HTMLInputElement>,
    user: UserMutation
  ) => {
    if (e.target.checked) append(user);
    else {
      const userIndex = getValues('companies').findIndex(
        ({ id }) => id === user.id
      );

      if (userIndex !== -1) remove(userIndex);
    }
  };

  const handleDeleteCompanies = async (companies: UserMutation[]) => {
    // The trigger is NOT unwrapped, so a body-level failure resolves normally.
    // Classify it before claiming anything happened: the old code toasted
    // "you have successfully deleted N companies!" for a rejected delete too.
    const response = await deleteTeamMembers(
      companies.map(({ id }) => ({ id }))
    );

    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title: 'Could not delete the selected companies',
        description:
          'No company was deleted. Please try again or contact BrickLynk support.',
      });
      return;
    }

    displayNotification({
      title: `you have successfully deleted ${companies.length} companies!`,
      description: `Your have deleted (${companies
        .map(({ name }, index) => {
          if (companies.length === 1 || companies.length === index + 1)
            return name;
          // Was a bare expression with no `return` (same defect as Users.tsx),
          // so every name except the last vanished in the join.
          else return ` ${name} -`;
        })
        .join('')}).`,
    });
  };

  return (
    <Box h="full">
      {/* {isOpen && (
        <FilterDrawer
          isOpen={isOpen}
          onClose={handleFilterDrawerClose}
          filters={userFilters}
        />
      )} */}

      {isDeleteCompaniesModalOpen && (
        <DeleteUsersModal
          isOpen={isDeleteCompaniesModalOpen}
          onClose={isDeleted => {
            if (isDeleted) void handleDeleteCompanies(getValues('companies'));
            onCloseDeleteCompaniesModal();
          }}
        />
      )}

      <VStack align="stretch">
        <HStack justify="space-between" mt="4" align="start">
          <HStack align="baseline">
            <Heading
              color="custom_blue.1"
              fontSize="2xl"
              textTransform="capitalize"
            >
              {status !== 'all' ? status : ''} Companies
            </Heading>
            <Box color="custom_gray.1" fontSize="sm">
              ({data?.data?.length})
            </Box>
          </HStack>

          <HStack>
            <Menu closeOnSelect={false}>
              {fields.length
                ? ({ isOpen }) => (
                    <>
                      <MenuButton
                        as={Button}
                        rightIcon={
                          isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />
                        }
                        fontSize="sm"
                        bg="none"
                        _hover={{ bg: 'none' }}
                        border="1px"
                        borderColor="custom_gray.1"
                      >
                        Actions
                      </MenuButton>
                      <MenuList p="0">
                        <MenuItem
                          onClick={onOpenDeleteCompaniesModal}
                          border="1px"
                          borderColor="custom_gray.4"
                          p="3"
                        >
                          <HStack>
                            <Image src={RedTrashImage} alt="Delete companies" />
                            <Text
                              color="error.600"
                              fontSize="sm"
                              fontWeight="medium"
                            >
                              Delete
                            </Text>
                          </HStack>
                        </MenuItem>
                      </MenuList>
                    </>
                  )
                : null}
            </Menu>
            <Can I="POST" a="teamMember">
              <Button
                onClick={() => navigate(`/companies/new-company`)}
                bg="custom_orange.2"
                _hover={{ bg: 'custom_orange.2' }}
                color="white"
                fontWeight="semibold"
                fontSize="sm"
              >
                <Image src={PlusIcon} alt="plus icon" boxSize="5" mr="1" />
                Create New Company
              </Button>
            </Can>
          </HStack>
        </HStack>

        <VStack
          align="stretch"
          bg="white"
          border="1px"
          borderColor="custom_gray.3"
          my="4"
          p="3"
          gap="3"
          borderRadius="md"
          minH="80vh"
        >
          <HStack py="2">
            <InputGroup flexBasis="45%">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search"
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            {/* <Button
            leftIcon={<FiltersIcon />}
            color="custom_blue.1"
            fontWeight="semibold"
            fontSize="sm"
            boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
            border="1px"
            borderColor="custom_blue.3"
            bg="none"
            _hover={{ bg: 'none' }}
            onClick={onOpen}
          >
            Filters
          </Button> */}
          </HStack>

          {/* {Object.entries(filters).map(([key, value]) => {
          if (value)
            return (
              <HStack key={key}>
                <RoundedBadge
                  withCloseButton
                  onClose={() =>
                    handleRemoveFilter(key as keyof typeof filters)
                  }
                >
                  {value}
                </RoundedBadge>
              </HStack>
            );
        })} */}

          {/* <FilterBadges onRemoveFilter={handleRemoveFilter} filters={filters} /> */}

          {isLoading || isFetching ? (
            <CenteredSpinner />
          ) : (
            <TableContainer
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              minH="70vh"
            >
              <Table variant="simple">
                <Thead bg="custom_gray.2">
                  <Tr>
                    <Th
                      border="none"
                      color="custom_blue.1"
                      fontWeight="medium"
                      fontSize="xs"
                      letterSpacing="normal"
                      textTransform="none"
                    >
                      ID
                    </Th>
                    <Th
                      border="none"
                      color="custom_blue.1"
                      fontWeight="medium"
                      fontSize="xs"
                      letterSpacing="normal"
                      textTransform="none"
                    >
                      Name
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data?.data?.map(({ id, name }) => (
                    <Tr key={id}>
                      <Td>{id}</Td>
                      <Td>{name}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </VStack>
      </VStack>
    </Box>
  );
};
